import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import ContractValueDashboard from "@/components/ContractValueDashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RecordForm } from "@/components/DataModule";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Repeat, CalendarPlus, Loader2, Zap } from "lucide-react";

const FREQ = ["Weekly", "Fortnightly", "Monthly", "Quarterly", "Six Monthly", "Annual"];
const FIELDS = [
  { key: "title", label: "Job title", required: true },
  { key: "service_type", label: "Service type", required: true },
  { key: "customer_id", label: "Client", type: "lookup", entity: "customers", labelKey: "name", mirror: "customer_name" },
  { key: "site_id", label: "Site", type: "lookup", entity: "sites", labelKey: "name", mirror: "site_name" },
  { key: "frequency", label: "Frequency", type: "select", options: FREQ, default: "Monthly" },
  { key: "priority", label: "Priority", type: "select", options: ["Low", "Normal", "High"], default: "Normal" },
  { key: "start_date", label: "Start date", type: "date" },
  { key: "end_date", label: "End date", type: "date" },
  { key: "preferred_time", label: "Preferred time" },
  { key: "description", label: "Description", type: "textarea" },
];

const FREQ_DAYS = { Daily: 1, Weekly: 7, Fortnightly: 14 };
const FREQ_MONTHS = { Monthly: 1, Quarterly: 3, "Six Monthly": 6, Annual: 12 };
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const plannedVisits = (f) => {
  if (!f.start_date || !f.end_date) return 0;
  let occ = new Date(f.start_date.slice(0, 10)); const end = new Date(f.end_date.slice(0, 10));
  const freq = f.frequency || "Monthly";
  let n = 0, guard = 0;
  while (occ <= end && guard < 500) {
    n += 1; guard += 1;
    occ = FREQ_DAYS[freq] ? new Date(occ.getTime() + FREQ_DAYS[freq] * 86400000) : addMonths(occ, FREQ_MONTHS[freq] || 1);
  }
  return n;
};
const perVisitValue = (f) => {
  const amt = parseFloat(f.value_amount);
  if (!amt || amt <= 0) return null;
  if (f.value_type === "total") { const n = plannedVisits(f); return n ? Math.round((amt / n) * 100) / 100 : null; }
  return amt;
};

export default function JobTemplates() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { company } = useTenant();
  const cur = company?.currency || "EUR";
  const canSeeValue = ["amden_super_admin", "ops_owner", "finance"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [initialForm, setInitialForm] = useState("{}");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const endBadge = (t) => {
    if (!t.end_date) return null;
    const days = Math.ceil((new Date(t.end_date) - new Date()) / 86400000);
    if (days < 0) return <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-red-100 text-red-700">Ended</span>;
    if (days <= 30) return <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">Ends in {days}d</span>;
    return null;
  };

  const load = useCallback(() => { api.get("/job-templates").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && rows) {
      const t = rows.find((x) => x.id === openId);
      if (t) openEdit(t);
      else toast.error("That recurring work no longer exists");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  const openNew = () => { const def = { frequency: "Monthly", priority: "Normal", value_type: "per_visit" }; setForm(def); setInitialForm(JSON.stringify(def)); setEditing(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t }); setInitialForm(JSON.stringify({ ...t })); setEditing(t); setOpen(true); };

  const requestClose = () => {
    if (JSON.stringify(form) !== initialForm) setConfirmDiscard(true);
    else setOpen(false);
  };

  const save = async () => {
    if (!form.title || !form.service_type) { toast.error("Title and service type required"); return; }
    setSaving(true);
    const payload = { ...form, value_type: form.value_type || "per_visit", value_amount: form.value_amount === "" || form.value_amount == null ? null : parseFloat(form.value_amount) };
    try {
      if (editing) await api.put(`/job-templates/${editing.id}`, payload);
      else await api.post("/job-templates", payload);
      toast.success("Template saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const generate = async (t) => {
    setBusy(t.id);
    try { const { data } = await api.post(`/job-templates/${t.id}/generate`); toast.success(`${data.created} visit(s) generated → Unscheduled Work`); load(); }
    catch (e) { toast.error("Generate failed"); } finally { setBusy(null); }
  };

  const generateAll = async () => {
    setBusy("all");
    try { const { data } = await api.post("/job-templates/generate-all"); toast.success(`${data.created} recurring visit(s) generated`); load(); }
    finally { setBusy(null); }
  };

  const renew = async (t) => {
    setBusy(t.id);
    try { const { data } = await api.post(`/job-templates/${t.id}/extend`, { months: 12 }); toast.success(`${t.ref} extended to ${new Date(data.end_date).toLocaleDateString()} · ${data.created} new visit(s)`); load(); }
    catch (e) { toast.error("Extend failed"); } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-[#0F1725] grid place-items-center text-white"><Repeat className="h-5 w-5" /></div>
          <div><h1 className="text-2xl font-head font-bold text-slate-900">Recurring Works</h1>
            <p className="text-sm text-slate-500">Ongoing service requirements — the source of recurring scheduled visits</p></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="templates-generate-all" onClick={generateAll} disabled={busy === "all"}>{busy === "all" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}Generate all visits</Button>
          <Button className="btn-accent" data-testid="template-new-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" />New Recurring Work</Button>
        </div>
      </div>

      <Tabs defaultValue="works">
        {canSeeValue && (
          <TabsList className="mb-3">
            <TabsTrigger value="works" data-testid="tab-recurring-works">Recurring Works</TabsTrigger>
            <TabsTrigger value="value" data-testid="tab-contract-value">Contract Value</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="works">
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>{["Recurring Work", "Service", "Frequency", "Priority", "Value", "Visits", "Schedule", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-4 py-3 whitespace-nowrap uppercase text-xs tracking-wide">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">No recurring works yet. Create one to auto-generate recurring visits.</td></tr>}
            {rows && rows.map((t) => (
              <tr key={t.id} data-testid={`template-row-${t.id}`} className="data-row border-t border-slate-100">
                <td className="px-4 py-3 cursor-pointer" onClick={() => openEdit(t)}>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">{t.title}{endBadge(t)}</div>
                  <div className="text-xs text-slate-400">{t.customer_name} · {t.site_name}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{t.service_type}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 accent-text font-medium"><Repeat className="h-3.5 w-3.5" />{t.frequency}</span></td>
                <td className="px-4 py-3"><span className="inline-flex rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600">{t.priority}</span></td>
                <td className="px-4 py-3 whitespace-nowrap" data-testid={`template-value-${t.id}`}>
                  {t.per_visit_value ? (
                    <div>
                      <div className="font-semibold text-slate-800">{money(t.per_visit_value)}<span className="text-xs font-normal text-slate-400"> /visit</span></div>
                      {t.value_type === "total" && <div className="text-[11px] text-slate-400">{money(t.value_amount)} total</div>}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-800">{t.visits}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button data-testid={`template-generate-${t.id}`} title="Generate visits" onClick={() => generate(t)} disabled={busy === t.id} className="text-slate-400 hover:accent-text transition-colors">
                      {busy === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
                    </button>
                    {endBadge(t) && (
                      <button data-testid={`template-renew-${t.id}`} onClick={() => renew(t)} disabled={busy === t.id}
                        className="text-xs font-medium accent-text hover:underline">Renew +12m</button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3"><button data-testid={`template-view-jobs-${t.id}`} onClick={() => nav(`/app/jobs?template=${t.id}&tref=${encodeURIComponent(t.ref || "")}`)} className="text-xs accent-text hover:underline whitespace-nowrap">View generated jobs →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>
        {canSeeValue && (
          <TabsContent value="value">
            <ContractValueDashboard cur={cur} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); else setOpen(o); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? `Edit Recurring Work · ${editing.ref}` : "New Recurring Work"}</DialogTitle></DialogHeader>
          <RecordForm fields={FIELDS} value={form} onChange={setForm} />
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Contract value</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-xs text-slate-600">Value type</Label>
                <Select value={form.value_type || "per_visit"} onValueChange={(v) => setForm((f) => ({ ...f, value_type: v }))}>
                  <SelectTrigger data-testid="template-value-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="per_visit">Cost per visit</SelectItem>
                    <SelectItem value="total">Total contract value (spread across visits)</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs text-slate-600">Amount (€)</Label>
                <Input type="number" min="0" step="0.01" data-testid="template-value-amount" value={form.value_amount ?? ""} placeholder="0.00"
                  onChange={(e) => setForm((f) => ({ ...f, value_amount: e.target.value }))} className="mt-1 bg-white" /></div>
            </div>
            {(() => {
              const amt = parseFloat(form.value_amount);
              if (!amt || amt <= 0) return <p className="text-[11px] text-slate-400 mt-2">Each generated visit will carry this value into billing.</p>;
              const pv = perVisitValue(form);
              if (form.value_type === "total") {
                const n = plannedVisits(form);
                if (!n) return <p data-testid="template-value-preview" className="text-xs text-amber-600 mt-2">Set a start and end date so the total can be spread across visits.</p>;
                return <p data-testid="template-value-preview" className="text-xs accent-text font-medium mt-2">{money(amt)} ÷ {n} visit{n > 1 ? "s" : ""} = {money(pv)} per visit</p>;
              }
              return <p data-testid="template-value-preview" className="text-xs accent-text font-medium mt-2">{money(pv)} per visit</p>;
            })()}
          </div>
          <DialogFooter><Button variant="outline" data-testid="template-cancel-btn" onClick={requestClose}>Cancel</Button>
            <Button className="btn-accent" data-testid="template-save-btn" onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Recurring Work</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>You have unsaved edits — closing will lose them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="keep-editing-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-changes-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setConfirmDiscard(false); setOpen(false); }}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
